/**
 * Attendance tab: per-learner, per-session attendance from AMS via the
 * Supergraph (browser-direct, GRMSB-785 pattern). Read-only — attendance
 * marking/overrides stay in MyClass / Instructor App.
 *
 * Answers the field pain: "all students 'partially attended' → Grimsby 0
 * attendees → can't close and bill" by showing the rollup rule and the raw
 * per-session attendanceDurationPercentage behind every disputed mark.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Spinner,
    Table,
    StatusIndicator,
    Box,
    SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import {
    loadAttendanceRoster,
    selectAttendanceRoster,
    selectIsAttendanceLoading,
    selectSelectedActivity,
} from '../../../../store/slices/selectedActivitySlice';
import { LearnerAttendance } from '../../../../../common/interfaces/supergraph';
import { formatString } from '../../../../../common/utils/formatString';

/**
 * Display mapping for AMS AttendanceStatus. The 60% threshold rule is AMS
 * internal — we describe it in the legend but never compute it ourselves.
 */
const STATUS_DISPLAY: Record<
    string,
    {
        label: string;
        type: 'success' | 'warning' | 'error' | 'stopped' | 'pending';
    }
> = {
    ATTENDED: { label: 'Attended', type: 'success' },
    PARTIALLY_ATTENDED: { label: 'Partially attended', type: 'warning' },
    NO_SHOW: { label: 'No show', type: 'error' },
    NOT_MARKED: { label: 'Not marked', type: 'pending' },
    REMOVED: { label: 'Removed', type: 'stopped' },
};

const statusIndicator = (status: string) => {
    const display = STATUS_DISPLAY[status];
    return display ? (
        <StatusIndicator type={display.type}>{display.label}</StatusIndicator>
    ) : (
        formatString(status)
    );
};

const formatSessionDate = (timestamp: number | null): string => {
    if (!timestamp) {
        return '-';
    }
    // AWSTimestamp is epoch seconds.
    return new Date(timestamp * 1000).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    });
};

const sessionSummary = (learner: LearnerAttendance) => {
    if (learner.sessions.length === 0) {
        return '-';
    }
    return (
        <SpaceBetween direction="horizontal" size="xs">
            {learner.sessions.map((session, index) => (
                <Box key={index} display="inline-block">
                    <Box variant="small">
                        {formatSessionDate(session.sessionStartTime)}
                        {': '}
                        {session.attendanceDurationPercentage !== null
                            ? `${Math.round(session.attendanceDurationPercentage)}%`
                            : (STATUS_DISPLAY[session.attendanceStatus]
                                  ?.label ?? session.attendanceStatus)}
                    </Box>
                </Box>
            ))}
        </SpaceBetween>
    );
};

const AttendanceTab = () => {
    const dispatch = useDispatch();
    const roster = useSelector(selectAttendanceRoster);
    const isLoading = useSelector(selectIsAttendanceLoading);
    const activityPk = useSelector(selectSelectedActivity)?.activity?.pk;

    useEffect(() => {
        // Load on demand; keyed on the activity pk so navigating to a
        // different activity refetches its roster (getSelectedActivity clears
        // the previous roster, so roster === null holds after navigation).
        if (roster === null && !isLoading) {
            dispatch(loadAttendanceRoster());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, activityPk]);

    if (isLoading) {
        return <Spinner data-testid="AttendanceTabSpinner" size="large" />;
    }

    if (roster === null) {
        return (
            <Alert
                data-testid="AttendanceTabUnavailable"
                statusIconAriaLabel="Info"
            >
                Attendance detail is currently unavailable. The counts shown on
                the Details tab are unaffected.
            </Alert>
        );
    }

    const counts = roster.reduce(
        (acc, learner) => {
            acc[learner.rollupStatus] = (acc[learner.rollupStatus] ?? 0) + 1;
            return acc;
        },
        {} as Record<string, number>,
    );

    return (
        <SpaceBetween size="m">
            <Alert statusIconAriaLabel="Info" data-testid="AttendanceLegend">
                Attendance is marked automatically from session join data:{' '}
                <strong>60% or more</strong> of session time = Attended, less
                than 60% = Partially attended, 0% = No show. Not marked is the
                initial state. Attendance is managed in MyClass / Instructor App
                — this view is read-only.
            </Alert>
            <Box data-testid="AttendanceSummary">
                {Object.entries(STATUS_DISPLAY).map(([status, display]) =>
                    counts[status] ? (
                        <Box
                            key={status}
                            display="inline-block"
                            margin={{ right: 'l' }}
                        >
                            <StatusIndicator type={display.type}>
                                {display.label}: {counts[status]}
                            </StatusIndicator>
                        </Box>
                    ) : null,
                )}
            </Box>
            <Table
                data-testid="AttendanceRosterTable"
                columnDefinitions={[
                    {
                        id: 'learner',
                        header: 'Learner',
                        cell: (learner: LearnerAttendance) =>
                            formatString(
                                learner.learnerName || learner.learnerEmail,
                            ),
                    },
                    {
                        id: 'email',
                        header: 'Email',
                        cell: (learner: LearnerAttendance) =>
                            formatString(learner.learnerEmail),
                    },
                    {
                        id: 'status',
                        header: 'Attendance',
                        cell: (learner: LearnerAttendance) =>
                            statusIndicator(learner.rollupStatus),
                    },
                    {
                        id: 'sessions',
                        header: 'Per-session attendance',
                        cell: sessionSummary,
                    },
                ]}
                items={roster}
                empty={
                    <Box textAlign="center">
                        No attendance records for this activity.
                    </Box>
                }
            />
        </SpaceBetween>
    );
};

export default AttendanceTab;
